import _createForOfIteratorHelper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _toConsumableArray from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import { Command } from 'ckeditor5/src/core.js';
import { splitListItemBefore, expandListBlocksToCompleteItems, getListItemBlocks, getListItems, removeListAttributes, outdentFollowingItems, ListItemUid, sortBlocks, getSelectedBlockObject, isListItemBlock, canBecomeSimpleListItem } from './utils/model.js';
/**
 * The list command. It is used by the {@link module:list/list~List list feature}.
 */
var ListCommand = /*#__PURE__*/function (_Command) {
  _inherits(ListCommand, _Command);
  var _super = _createSuper(ListCommand);
  /**
   * Creates an instance of the command.
   *
   * @param editor The editor instance.
   * @param type List type that will be handled by this command.
   */
  function ListCommand(editor, type) {
    var _this;
    _classCallCheck(this, ListCommand);
    _this = _super.call(this, editor);
    _this.type = type;
    return _this;
  }
  /**
   * @inheritDoc
   */
  _createClass(ListCommand, [{
    key: "refresh",
    value: function refresh() {
      this.value = this._getValue();
      this.isEnabled = this._checkEnabled();
    }
    /**
     * Executes the list command.
     *
     * @fires execute
     * @fires afterExecute
     * @param options Command options.
     * @param options.forceValue If set, it will force the command behavior. If `true`, the command will try to convert the
     * selected items and potentially the neighbor elements to the proper list items. If set to `false` it will convert selected elements
     * to paragraphs. If not set, the command will toggle selected elements to list items or paragraphs, depending on the selection.
     */
  }, {
    key: "execute",
    value: function execute() {
      var _this2 = this;
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var model = this.editor.model;
      var document = model.document;
      var selectedBlockObject = getSelectedBlockObject(model);
      var blocks = Array.from(document.selection.getSelectedBlocks()).filter(function (block) {
        return model.schema.checkAttribute(block, 'listType') || canBecomeSimpleListItem(block, model.schema);
      });
      // Whether we are turning off some items.
      var turnOff = options.forceValue !== undefined ? !options.forceValue : this.value;
      model.change(function (writer) {
        if (turnOff) {
          var lastBlock = blocks[blocks.length - 1];
          // Split the first block from the list item.
          var itemBlocks = getListItemBlocks(lastBlock, {
            direction: 'forward'
          });
          var changedBlocks = [];
          if (itemBlocks.length > 1) {
            changedBlocks.push.apply(changedBlocks, _toConsumableArray(splitListItemBefore(itemBlocks[1], writer)));
          }
          // Strip list attributes.
          changedBlocks.push.apply(changedBlocks, _toConsumableArray(removeListAttributes(blocks, writer)));
          // Outdent items following the selected list item.
          changedBlocks.push.apply(changedBlocks, _toConsumableArray(outdentFollowingItems(lastBlock, writer)));
          _this2._fireAfterExecute(changedBlocks);
        }
        // Changing type of list items for a collapsed selection inside a list item.
        else if ((selectedBlockObject || document.selection.isCollapsed) && isListItemBlock(blocks[0])) {
          var _changedBlocks = getListItems(selectedBlockObject || blocks[0]);
          var _iterator = _createForOfIteratorHelper(_changedBlocks),
            _step;
          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var block = _step.value;
              writer.setAttribute('listType', _this2.type, block);
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }
          _this2._fireAfterExecute(_changedBlocks);
        }
        // Turning on the list items for a non-collapsed selection.
        else {
          var _changedBlocks2 = [];
          var _iterator2 = _createForOfIteratorHelper(blocks),
            _step2;
          try {
            for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
              var _block = _step2.value;
              // Promote the given block to the list item.
              if (!_block.hasAttribute('listType')) {
                // Rename block to a simple list item if this option is enabled.
                if (!_block.is('element', 'listItem') && canBecomeSimpleListItem(_block, model.schema)) {
                  writer.rename(_block, 'listItem');
                }
                writer.setAttributes({
                  listIndent: 0,
                  listItemId: ListItemUid.next(),
                  listType: _this2.type
                }, _block);
                _changedBlocks2.push(_block);
              }
              // Change the type of list item.
              else {
                var _iterator3 = _createForOfIteratorHelper(expandListBlocksToCompleteItems(_block, {
                    withNested: false
                  })),
                  _step3;
                try {
                  for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
                    var node = _step3.value;
                    if (node.getAttribute('listType') != _this2.type) {
                      writer.setAttribute('listType', _this2.type, node);
                      _changedBlocks2.push(node);
                    }
                  }
                } catch (err) {
                  _iterator3.e(err);
                } finally {
                  _iterator3.f();
                }
              }
            }
          } catch (err) {
            _iterator2.e(err);
          } finally {
            _iterator2.f();
          }
          _this2._fireAfterExecute(_changedBlocks2);
        }
      });
    }
    /**
     * Fires the `afterExecute` event.
     *
     * @param changedBlocks The changed list elements.
     */
  }, {
    key: "_fireAfterExecute",
    value: function _fireAfterExecute(changedBlocks) {
      this.fire('afterExecute', sortBlocks(new Set(changedBlocks)));
    }
    /**
     * Checks the command's {@link #value}.
     *
     * @returns The current value.
     */
  }, {
    key: "_getValue",
    value: function _getValue() {
      var selection = this.editor.model.document.selection;
      var blocks = Array.from(selection.getSelectedBlocks());
      if (!blocks.length) {
        return false;
      }
      for (var _i = 0, _blocks = blocks; _i < _blocks.length; _i++) {
        var block = _blocks[_i];
        if (block.getAttribute('listType') != this.type) {
          return false;
        }
      }
      return true;
    }
    /**
     * Checks whether the command can be enabled in the current context.
     *
     * @returns Whether the command should be enabled.
     */
  }, {
    key: "_checkEnabled",
    value: function _checkEnabled() {
      var model = this.editor.model;
      var schema = model.schema;
      var selection = model.document.selection;
      var blocks = Array.from(selection.getSelectedBlocks());
      if (!blocks.length) {
        return false;
      }
      // If command value is true it means that we are in list item, so the command should be enabled.
      if (this.value) {
        return true;
      }
      for (var _i2 = 0, _blocks2 = blocks; _i2 < _blocks2.length; _i2++) {
        var block = _blocks2[_i2];
        if (schema.checkAttribute(block, 'listType') || canBecomeSimpleListItem(block, schema)) {
          return true;
        }
      }
      return false;
    }
  }]);
  return ListCommand;
}(Command);
export { ListCommand as default };